import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { Moment } from 'moment-timezone';
import moment from 'moment-timezone';

import type { Enrollment, Membership, MembershipItem } from '@spike/membership-model';
import { EnrollmentStatuses, MEMBERSHIP_ITEM_TYPES, MembershipItemType } from '@spike/membership-model';
import MembershipServiceSelect, {
    MembershipServiceSelectOption
} from 'components/CreateBooking/Memberships/UI/MembershipServiceSelect/MembershipServiceSelect';
import { isEnrollmentAvailableOnDate } from 'components/CreateBooking/utils/membershipDisplayPrice';
import { RootState } from 'store';
import { Spinner } from 'components/UI/V3/Spinner/Spinner';

import styles from './MembershipsSelector.module.scss';

export type MembershipsSelection = {
    membership: Membership;
    item: MembershipItem;
    enrollmentId?: number;
    appointmentFrom?: Moment;
};

export interface MembershipsSelectorProps {
    clientId: number;
    petId: number;
    date: Moment;
    onSelect?: (selection: MembershipsSelection) => void;
    petServicesLoading?: boolean;
}

function isServiceItem(item: MembershipItem): boolean {
    if (item.itemType === MembershipItemType.SERVICE || item.itemType === 'Service') return true;
    return (
        item.itemType === MEMBERSHIP_ITEM_TYPES.GROOMING_SERVICE ||
        item.itemType === MEMBERSHIP_ITEM_TYPES.DAYCARE_SERVICE ||
        item.itemType === MEMBERSHIP_ITEM_TYPES.BOARDING_SERVICE ||
        item.itemType === MEMBERSHIP_ITEM_TYPES.TRANSPORTATION_SERVICE ||
        item.itemType === MEMBERSHIP_ITEM_TYPES.WALKING_SERVICE ||
        item.itemType === MEMBERSHIP_ITEM_TYPES.TRAINING_SERVICE
    );
}

function remainingUses(item: MembershipItem): { remaining: number; total?: number } | null {
    if (item.unlimited) return { remaining: Number.POSITIVE_INFINITY };

    if (typeof item.left === 'number') {
        return { remaining: item.left, total: typeof item.quantity === 'number' ? item.quantity : undefined };
    }

    if (typeof item.available === 'number') {
        if (typeof item.consumed === 'number') {
            return { remaining: item.available - item.consumed, total: item.available };
        }
        return { remaining: item.available, total: undefined };
    }

    return null;
}

function isItemDisabled(item: MembershipItem): boolean {
    if (item.unlimited) return false;
    const info = remainingUses(item);
    if (!info) return false;
    return info.remaining <= 0;
}

function formatBadge(item: MembershipItem): string {
    if (item.discountPercentage != null && item.discountPercentage > 0) {
        return `${item.discountPercentage}%`;
    }
    if (item.discountAmount != null && item.discountAmount > 0) {
        return `$${Number(item.discountAmount).toFixed(0)}`;
    }
    if (item.unlimited) {
        return 'Unlimited';
    }
    const info = remainingUses(item);
    if (info && Number.isFinite(info.remaining)) {
        const remaining = Math.max(0, info.remaining);
        if (info.total != null && Number.isFinite(info.total) && info.total > 0) {
            return `${remaining}/${info.total}`;
        }
        if (remaining > 0) return `${remaining}`;
    }
    return item.total ? String(item.total) : '—';
}

function itemKindLabel(item: MembershipItem): string {
    if (item.discountPercentage != null && item.discountPercentage > 0) {
        return 'Discount-based';
    }
    if (item.discountAmount != null && item.discountAmount > 0) {
        return 'Discount-based';
    }
    return 'Included';
}

function selectionKey(membership: Membership, item: MembershipItem, enrollmentId?: number): string {
    return `${enrollmentId ?? '—'}-${membership.id ?? 0}-${item.id ?? item.itemID}`;
}

function rowsEqual(
    a: MembershipsSelection | null,
    b: { membership: Membership; item: MembershipItem; enrollmentId: number }
): boolean {
    if (!a) return false;
    return selectionKey(a.membership, a.item, a.enrollmentId) === selectionKey(b.membership, b.item, b.enrollmentId);
}

export const MembershipsSelector: React.FC<MembershipsSelectorProps> = ({
    clientId,
    petId,
    date,
    onSelect,
    petServicesLoading = false
}) => {
    const enrollments = useSelector<RootState, Enrollment[]>(state => state.memberships.enrollments);

    const [selected, setSelected] = useState<MembershipsSelection | null>(null);

    useEffect(() => {
        setSelected(null);
    }, [petId]);

    const { rows, options } = useMemo(() => {
        const nextRows: Array<{
            membership: Membership;
            item: MembershipItem;
            enrollmentId: number;
            disabled: boolean;
            pastDue: boolean;
        }> = [];
        for (const enrollment of enrollments) {
            if (!enrollment.pets?.some(p => p.clientId === clientId)) continue;
            if (!enrollment.pets?.some(p => p.id === petId)) continue;
            const membership = enrollment.membership;
            const items = membership.membershipItems?.filter(isServiceItem) ?? [];
            const disabledByRange = !isEnrollmentAvailableOnDate(enrollment, date, date.tz() || moment.tz.guess());
            const disabledByPending = enrollment.status === EnrollmentStatuses.pending;
            const disabledByPastDue = enrollment.status === EnrollmentStatuses.pastDue;
            const isCanceled = enrollment.status === EnrollmentStatuses.canceled;
            const disabledByInactiveItem = enrollment.status !== EnrollmentStatuses.active && !isCanceled;

            const tz = date.tz() || moment.tz.guess();
            const selectedDay = date.clone().tz(tz).startOf('day');
            const canceledEnd = enrollment.endDate
                ? moment(enrollment.endDate as any)
                      .tz(tz)
                      .endOf('day')
                : null;
            const disabledByCanceled =
                isCanceled && (!canceledEnd?.isValid() || selectedDay.isAfter(canceledEnd, 'day'));
            const disabledByPaused = enrollment.status === EnrollmentStatuses.paused;
            const disabledByInactive = membership.active === false;
            for (const item of items) {
                nextRows.push({
                    membership,
                    item,
                    enrollmentId: enrollment.id,
                    pastDue: disabledByPastDue,
                    disabled:
                        disabledByRange ||
                        disabledByPending ||
                        disabledByPastDue ||
                        disabledByInactive ||
                        isItemDisabled(item) ||
                        disabledByInactiveItem ||
                        disabledByCanceled ||
                        disabledByPaused
                });
            }
        }

        const nextOptions: MembershipServiceSelectOption[] = nextRows.map((row, index) => {
            const inactive = row.membership.active === false;
            const unavailable = inactive || row.pastDue || row.disabled;
            return {
                id: index,
                serviceTitle: row.item.itemName,
                membershipName: row.membership.name,
                kindLabel: unavailable ? 'Unavailable' : itemKindLabel(row.item),
                badgeText: unavailable ? 'Unavailable' : formatBadge(row.item),
                disabled: row.disabled
            };
        });

        return { rows: nextRows, options: nextOptions };
    }, [enrollments, clientId, petId, date]);

    const selectedOption = useMemo((): MembershipServiceSelectOption | null => {
        if (!selected) return null;
        const idx = rows.findIndex(r => rowsEqual(selected, r));
        if (idx < 0) return null;
        return options[idx] ?? null;
    }, [selected, rows, options]);

    const handleSelect = (opt: MembershipServiceSelectOption) => {
        const row = rows[opt.id];
        if (!row || row.disabled) return;
        const next: MembershipsSelection = {
            membership: row.membership,
            item: row.item,
            enrollmentId: row.enrollmentId,
            appointmentFrom: date.clone()
        };
        setSelected(next);
        onSelect?.(next);
    };

    return (
        <div className={styles.root}>
            <label className={styles.label}>Services</label>
            <div className={styles.selectWrap}>
                {petServicesLoading && (
                    <div
                        className={styles.loadingOverlay}
                        aria-busy="true"
                        aria-live="polite"
                        aria-label="Loading services"
                    >
                        <Spinner size={32} />
                    </div>
                )}
                {options.length === 0 ? (
                    <div className={styles.empty}>No membership services available</div>
                ) : (
                    <MembershipServiceSelect
                        placeholder="Select a Service"
                        selected={selectedOption}
                        options={options}
                        onSelect={handleSelect}
                        className={styles.select}
                    />
                )}
            </div>
        </div>
    );
};

export default MembershipsSelector;
